import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { fmtD, fmtDT } from "../utils/period";
import { toStation } from "../utils/date";
import type { SystemOut } from "../types";

const LABEL = { margin: "0 2px 6px", fontSize: 12, color: "var(--hint)", textTransform: "uppercase" as const, letterSpacing: ".04em" };
const ROW = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, padding: "10px 0" };
const SMALL_BTN = { marginTop: 10, background: "var(--bg)" };
const GROUP_COLOR: Record<string, string> = { v2_sec: "#2a78d6", v2_min: "#8fb8ec", old: "#ff9500", pa: "#1baf7a", other: "#8593a0" };
const KIND: Record<string, string> = {
  full_set: "system.k_full_set", v2_tables_step: "system.k_v2_tables_step", v2_tables_import: "system.k_v2_tables_import",
  v1_tables: "system.k_v1_tables", other: "system.k_other",
};

// «Управление → Система» (admin): services, server, database, backups, reports and
// events at a glance; «Сделать бэкап сейчас», the nightly-backup switch and
// «Пересчитать события». Server times are real UTC, shown as station time.
export default function SystemPanel() {
  const { t, lang } = useT();
  const [d, setD] = useState<SystemOut | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [backing, setBacking] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const load = useCallback(() => api.get<SystemOut>("/api/system").then(r => { setD(r); return r; }), []);
  useEffect(() => { load().catch((e: ApiError) => setErr(e.message)); }, [load]);

  const num = (x: number, digits = 1) => { const s = x.toFixed(digits); return lang === "ru" ? s.replace(".", ",") : s; };
  const size = (b?: number | null) => (b == null ? "—" : b >= 2 ** 30 ? `${num(b / 2 ** 30)} ${t("u.gb")}` : `${Math.max(1, Math.round(b / 2 ** 20))} ${t("u.mb")}`);
  const dt = (iso?: string | null) => (iso ? fmtDT(toStation(iso)).slice(0, 16) : "—");
  const day = (iso?: string | null) => (iso ? fmtD(toStation(iso)) : "—");
  const rows = (n: number) => (n >= 1e6 ? t("system.rows_m", { n: num(n / 1e6, 2) })
    : t("system.rows", { n: Math.round(n).toLocaleString(lang === "ru" ? "ru-RU" : "en-US") }));
  const fail = (e: unknown) => { setErr((e as ApiError).message); tg()?.HapticFeedback?.notificationOccurred("error"); };

  const backupNow = async () => {
    setBacking(true); setErr(null); setMsg(null);
    // Done = the service ran and stopped; success = a new dump appeared. (systemd forgets the
    // exit time of a oneshot unit nothing references, e.g. with the nightly timer off.)
    const before = d?.backups.nightly[0]?.name ?? null;
    try {
      await api.post("/api/system/backup");
      let seenRunning = false;
      for (let i = 0; i < 60; i++) {  // the prod DB takes ~40 s
        await new Promise(r => setTimeout(r, 3000));
        const r = await load();
        const running = r.backups.last_run.running;
        const newest = r.backups.nightly[0];
        seenRunning = seenRunning || running;
        if (!running && (seenRunning || (newest && newest.name !== before))) {
          const ok = !!newest && newest.name !== before;
          setMsg(ok ? t("system.backup_done", { size: size(newest.bytes) }) : t("system.backup_failed"));
          tg()?.HapticFeedback?.notificationOccurred(ok ? "success" : "error");
          break;
        }
      }
    } catch (e) { fail(e); } finally { setBacking(false); }
  };
  const toggle = async (on: boolean) => {
    setToggling(true); setErr(null); setMsg(null);
    try { await api.post("/api/system/backup/schedule", { enabled: on }); await load(); tg()?.HapticFeedback?.selectionChanged?.(); }
    catch (e) { fail(e); } finally { setToggling(false); }
  };
  const rebuild = async () => {
    setRebuilding(true); setErr(null); setMsg(null);
    try {
      const r = await api.post<Record<string, number>>("/api/events/rebuild", undefined, 120_000);
      setMsg(t("system.rebuilt", { n: Object.values(r).reduce((a, b) => a + b, 0) }));
      await load();
      tg()?.HapticFeedback?.notificationOccurred("success");
    } catch (e) { fail(e); } finally { setRebuilding(false); }
  };

  if (!d) return err ? <div className="alert error">{err}</div> : <div className="empty-state"><span className="spinner" /></div>;
  const b = d.backups;
  const lastBad = !!b.last_run.at && !!b.last_run.result && b.last_run.result !== "success";
  const svcSub = (key: string, since: string | null) =>
    key === "web" && d.cert ? t("system.cert", { d: day(d.cert.until) })
      : (key === "db" ? "PostgreSQL 16 · " : "") + (since ? t("system.since", { t: dt(since) }) : "—");
  const dbTotal = d.database.groups.reduce((a, g) => a + g.bytes, 0) || 1;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: "var(--hint)" }}>{t("system.at", { t: dt(d.at) })}</span>
        <span style={{ fontSize: 13, color: "var(--link)", cursor: "pointer", whiteSpace: "nowrap" }}
          onClick={() => { setMsg(null); load().catch(fail); }}>{t("system.refresh")}</span>
      </div>
      {err && <div className="alert error" style={{ marginBottom: 12 }}>{err}</div>}
      {msg && <div role="status" style={{ background: "rgba(52,199,89,0.14)", borderRadius: 12, padding: "10px 14px", marginBottom: 12, fontSize: 14 }}>{msg}</div>}

      <div style={LABEL}>{t("system.services")}</div>
      <div className="card" style={{ padding: "2px 14px 12px" }}>
        {d.services.map((s, i) => {
          const up = s.state === "active";
          return (
            <div key={s.key} style={{ ...ROW, borderTop: i ? "1px solid var(--border)" : "none" }}>
              <span style={{ width: 10, height: 10, marginTop: 6, borderRadius: 5, flex: "0 0 10px", background: up ? "#34c759" : "var(--destructive)" }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 600 }}>{t("system.svc_" + s.key, { bot: d.bot })}</span>
                <span style={{ display: "block", fontSize: 13, color: "var(--hint)" }}>{svcSub(s.key, s.since)}</span>
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: up ? "#34c759" : "var(--destructive)", whiteSpace: "nowrap" }}>
                {up ? t("system.running") : t("system.down") + (s.state ? ` (${s.state})` : "")}
              </span>
            </div>
          );
        })}
        <div style={{ fontSize: 12, color: "var(--hint)" }}>{t("system.restart_hint")}</div>
      </div>

      <div style={LABEL}>{t("system.server")}</div>
      <div className="card" style={{ display: "grid", gap: 8, fontSize: 14 }}>
        {d.host.disk && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>{t("system.disk")}</span>
              <b style={{ fontVariantNumeric: "tabular-nums" }}>{t("system.of", { a: size(d.host.disk.used), b: size(d.host.disk.total) })}</b></div>
            <div style={{ height: 6, borderRadius: 3, background: "var(--border)", overflow: "hidden", marginTop: 4 }}>
              <div style={{ width: `${(d.host.disk.used / d.host.disk.total) * 100}%`, height: 6, background: "#34c759" }} />
            </div>
          </div>
        )}
        {d.host.mem && <div style={{ display: "flex", justifyContent: "space-between" }}><span>{t("system.mem")}</span><b>{t("system.of", { a: size(d.host.mem.used), b: size(d.host.mem.total) })}</b></div>}
        {d.host.load != null && <div style={{ display: "flex", justifyContent: "space-between" }}><span>{t("system.load")}</span><b>{num(d.host.load, 2)} · {t("system.cores", { n: d.host.cpus ?? "—" })}</b></div>}
        {d.host.uptime_s != null && <div style={{ display: "flex", justifyContent: "space-between" }}><span>{t("system.uptime")}</span>
          <b>{t("system.uptime_v", { d: Math.floor(d.host.uptime_s / 86400), h: Math.floor((d.host.uptime_s % 86400) / 3600) })}</b></div>}
      </div>

      <div style={LABEL}>{t("system.db", { size: size(d.database.bytes) })}</div>
      <div className="card" style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", background: "var(--border)" }}>
          {d.database.groups.map(g => <div key={g.group} style={{ width: `${Math.max(0.5, (g.bytes / dbTotal) * 100)}%`, background: GROUP_COLOR[g.group] }} />)}
        </div>
        {d.database.groups.map(g => (
          <div key={g.group} style={{ display: "grid", gridTemplateColumns: "10px minmax(0, 1fr) auto", columnGap: 10, alignItems: "start" }}>
            <span style={{ width: 10, height: 10, marginTop: 5, borderRadius: 3, background: GROUP_COLOR[g.group] }} />
            <span>
              <span style={{ display: "block", fontSize: 14 }}>{t("system.g_" + g.group)}</span>
              <span style={{ display: "block", fontSize: 12, color: g.group === "old" ? "var(--yellow-text)" : "var(--hint)" }}>
                {g.group === "old" ? t("system.g_old_sub") : rows(g.rows)}
              </span>
            </span>
            <b style={{ fontSize: 14, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{size(g.bytes)}</b>
          </div>
        ))}
        <div style={{ fontSize: 12, color: "var(--hint)" }}>
          {d.database.last_import && t("system.last_import", { f: d.database.last_import.filename, to: d.database.last_import.to ? fmtDT(d.database.last_import.to).slice(0, 16) : "—" })}{" "}
          {d.database.analyzed_at && t("system.analyzed", { t: day(d.database.analyzed_at) })}
        </div>
      </div>

      <div style={LABEL}>{t("system.backups")}</div>
      <div className="card" style={{ display: "grid", gap: 10 }}>
        {(!b.timer.enabled || lastBad) && (
          <div style={{ padding: "10px 12px", borderRadius: 10, background: "var(--yellow)" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--yellow-text)" }}>{lastBad ? t("system.last_failed") : t("system.timer_off")}</div>
            {b.nightly[0] && <div style={{ fontSize: 13 }}>{t("system.last_ok", { t: dt(b.nightly[0].at), size: size(b.nightly[0].bytes) })}</div>}
          </div>
        )}
        <div>
          {[...b.nightly.slice(0, 5).map(f => ({ ...f, kind: "db" })), ...b.other].map((f, i) => (
            <div key={f.name} style={{ ...ROW, padding: "8px 0", borderTop: i ? "1px solid var(--border)" : "none" }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14 }}>{f.kind === "db" ? t("system.k_db") : t(KIND[f.kind ?? "other"] ?? "system.k_other")}</span>
                <span style={{ display: "block", fontSize: 12, color: "var(--hint)" }}>{f.kind === "db" ? dt(f.at) : day(f.at)}</span>
              </span>
              <b style={{ fontSize: 14, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{size(f.bytes)}</b>
            </div>
          ))}
          {b.nightly.length === 0 && b.other.length === 0 && <div style={{ fontSize: 13, color: "var(--hint)" }}>{t("system.no_backup")}</div>}
        </div>
        {b.timer.installed && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
            <span>
              <span style={{ display: "block", fontWeight: 600 }}>{t("system.nightly")}</span>
              <span style={{ display: "block", fontSize: 12, color: "var(--hint)" }}>
                {b.timer.enabled ? t("system.nightly_on", { t: dt(b.timer.next) }) : t("system.nightly_off")}
                {b.free != null ? " · " + t("system.free", { v: size(b.free) }) : ""}
              </span>
            </span>
            <button role="switch" aria-checked={b.timer.enabled} aria-label={t("system.nightly")} disabled={toggling || backing}
              onClick={() => toggle(!b.timer.enabled)}
              style={{ flex: "0 0 51px", position: "relative", width: 51, height: 31, padding: 0, border: "none", borderRadius: 16,
                       background: b.timer.enabled ? "#34c759" : "var(--border)", opacity: toggling ? 0.6 : 1, cursor: "pointer" }}>
              <span style={{ position: "absolute", top: 2, left: b.timer.enabled ? 22 : 2, width: 27, height: 27, borderRadius: 14,
                             background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.25)", transition: "left .15s" }} />
            </button>
          </div>
        )}
        <button className="btn secondary" style={SMALL_BTN} disabled={backing || !b.timer.installed} onClick={backupNow}>
          {backing ? <><span className="spinner" /> {t("system.backup_running")}</> : t("system.backup_now")}
        </button>
      </div>

      <div style={LABEL}>{t("system.reports")}</div>
      <div className="card" style={{ padding: "2px 14px 12px" }}>
        <div style={ROW}>
          <span><span style={{ display: "block", fontWeight: 600 }}>{t("system.pdf")}</span>
            <span style={{ display: "block", fontSize: 13, color: "var(--hint)" }}>{t("system.pdf_sub", { n: d.reports.count, d: d.reports.ttl_days })}</span></span>
          <b style={{ whiteSpace: "nowrap" }}>{size(d.reports.bytes)}</b>
        </div>
        <div style={{ ...ROW, borderTop: "1px solid var(--border)" }}>
          <span><span style={{ display: "block", fontWeight: 600 }}>{t("system.events")}</span>
            <span style={{ display: "block", fontSize: 13, color: "var(--hint)" }}>{t("system.events_sub", { t: dt(d.events.at) })}</span></span>
          <b>{d.events.count}</b>
        </div>
        <button className="btn secondary" style={{ ...SMALL_BTN, marginTop: 4 }} disabled={rebuilding} onClick={rebuild}>
          {rebuilding ? <span className="spinner" /> : t("system.rebuild")}
        </button>
      </div>
    </div>
  );
}
